import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as z from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { useToast } from "../ui/use-toast";
import { User } from "../../types/generated";
import { apiService } from "../../services/api";
import { UserSubscriptionsTab } from "./UserSubscriptionsTab";
import { UserAccountsTab } from "./UserAccountsTab";
import { Switch } from "../ui/switch";
import { Pencil } from "lucide-react";

const userFormSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  email: z.string().email("Invalid email address"),
  phone: z.string().optional(),
  password: z.string().optional(),
  admin: z.boolean(),
  active: z.boolean(),
  accounts_limit: z.number().min(0, "Account limit must be a positive number").optional(),
});

type UserFormValues = z.infer<typeof userFormSchema>;

interface UserDetailsDialogProps {
  user: User;
}

export function UserDetailsDialog({ user }: UserDetailsDialogProps) {
  const [open, setOpen] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const form = useForm<UserFormValues>({
    resolver: zodResolver(userFormSchema),
    defaultValues: {
      name: user.name,
      email: user.email,
      phone: user.phone || "",
      password: "",
      admin: user.admin,
      active: user.active,
      accounts_limit: user.settings?.accounts_limit || 0,
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: (data: Partial<User>) => apiService.updateUser(user.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast({
        title: "Success",
        description: "User updated successfully",
      });
      setOpen(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: UserFormValues) => {
    const updateData = { ...data };
    if (!updateData.password) {
      delete updateData.password;
    }
    updateUserMutation.mutate(updateData);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon">
          <Pencil className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent
        className="w-[calc(100vw-2rem)] sm:w-[85vw] md:w-[80vw] max-w-7xl p-4 sm:p-6 gap-4 h-[calc(100vh-4rem)] sm:h-[75vh] md:h-[80vh] flex flex-col overflow-hidden"
        aria-describedby="user-details-description"
      >
        <DialogHeader>
          <DialogTitle>User Details</DialogTitle>
          <p
            id="user-details-description"
            className="text-sm text-muted-foreground"
          >
            Manage user information, accounts, and subscriptions
          </p>
        </DialogHeader>

        <Tabs defaultValue="details" className="w-full flex-1 flex flex-col overflow-hidden">
          <TabsList className="flex h-auto w-full p-1 flex-shrink-0">
            <div className="grid grid-cols-3 w-full gap-1">
              <TabsTrigger value="details" className="w-full justify-center">
                User Details
              </TabsTrigger>
              <TabsTrigger value="accounts" className="w-full justify-center">
                Accounts
              </TabsTrigger>
              <TabsTrigger
                value="subscriptions"
                className="w-full justify-center"
              >
                Subscriptions
              </TabsTrigger>
            </div>
          </TabsList>

          <TabsContent
            value="details"
            className="flex-1 overflow-hidden relative"
          >
            <div className="absolute inset-0 overflow-y-auto py-4 px-1">
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-4"
                >
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input {...field} type="email" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex flex-col sm:flex-row sm:justify-between gap-4">
                  <FormField
                    control={form.control}
                    name="admin"
                    render={({ field }) => (
                      <FormItem className="flex items-center gap-2 min-w-[160px]">
                        <FormLabel>Admin Access</FormLabel>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="active"
                    render={({ field }) => (
                      <FormItem className="flex items-center gap-2 min-w-[160px]">
                        <FormLabel>Active Status</FormLabel>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="accounts_limit"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Account Limit</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="0"
                          {...field}
                          onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                        />
                      </FormControl>
                      <FormMessage />
                      <div className="text-xs text-muted-foreground">Maximum number of WhatsApp accounts this user can create</div>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="password"
                          placeholder="Leave empty to keep current password"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <DialogFooter>
                  <Button type="submit" disabled={updateUserMutation.isPending}>
                    {updateUserMutation.isPending
                      ? "Saving..."
                      : "Save Changes"}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
            </div>
          </TabsContent>

          <TabsContent
            value="accounts"
            className="flex-1 overflow-hidden relative"
          >
            <div className="absolute inset-0 overflow-y-auto py-4 px-1">
              <UserAccountsTab userId={user.id} userName={user.name} />
            </div>
          </TabsContent>

          <TabsContent
            value="subscriptions"
            className="flex-1 overflow-hidden relative"
          >
            <div className="absolute inset-0 overflow-y-auto py-4 px-1">
              <UserSubscriptionsTab userId={user.id} userName={user.name} />
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
